/**
 * FeatureFlags - Zentrale Definition aller Feature-Toggles
 *
 * Ermöglicht modulare Aktivierung/Deaktivierung von Features
 * wie Test-Mode, Pre-Game-Shop, etc.
 */

/**
 * Feature-Definitionen
 */
export const FEATURES = {
  // === System Features ===
  ALWAYS_ON: {
    id: 'ALWAYS_ON',
    name: 'Always On',
    description: 'Pseudo-Feature das immer aktiviert ist (für Phasen ohne Feature-Flag)',
    defaultEnabled: true,
    category: 'system',
  },

  // === Phasen-Features ===
  INTRO_SCREEN: {
    id: 'INTRO_SCREEN',
    name: 'Intro Screen',
    description: 'Zeigt einen animierten Intro-Bildschirm beim Spielstart',
    defaultEnabled: true,
    category: 'phases',
  },

  PRE_GAME_SHOP: {
    id: 'PRE_GAME_SHOP',
    name: 'Pre-Game Shop',
    description: 'Shop vor der ersten Runde zum Kaufen von Start-Jokern',
    defaultEnabled: false, // Nur im Test-Mode oder explizit aktiviert
    category: 'phases',
  },

  VICTORY_CEREMONY: {
    id: 'VICTORY_CEREMONY',
    name: 'Siegerehrung',
    description: 'Mehrstufige Siegerehrung am Spielende mit Statistiken',
    defaultEnabled: true, // Implementiert!
    category: 'phases',
  },

  // === Gameplay-Features ===
  SEAL_SYSTEM: {
    id: 'SEAL_SYSTEM',
    name: 'Siegel-System',
    description: 'Siegel können auf Karten angebracht werden',
    defaultEnabled: true,
    category: 'gameplay',
  },

  JOKER_SYSTEM: {
    id: 'JOKER_SYSTEM',
    name: 'Joker-System',
    description: 'Joker mit permanenten Effekten',
    defaultEnabled: true,
    category: 'gameplay',
  },

  CARD_EXCHANGE: {
    id: 'CARD_EXCHANGE',
    name: 'Kartentausch',
    description: 'Kartentausch zwischen Präsident und Asso',
    defaultEnabled: true,
    category: 'gameplay',
  },

  // === Entwicklungs-Features ===
  TEST_MODE: {
    id: 'TEST_MODE',
    name: 'Test Mode',
    description: 'Lokaler Test-Modus mit virtuellen Spielern',
    defaultEnabled: false,
    category: 'development',
    activates: ['PRE_GAME_SHOP', 'DEV_TOOLS'], // Aktiviert automatisch diese Features
  },

  DEV_TOOLS: {
    id: 'DEV_TOOLS',
    name: 'Developer Tools',
    description: 'Zeigt Entwickler-Panel mit Debug-Informationen',
    defaultEnabled: process.env.NODE_ENV === 'development',
    category: 'development',
  },

  DEBUG_LOGGING: {
    id: 'DEBUG_LOGGING',
    name: 'Debug Logging',
    description: 'Ausführliches Logging für Debugging',
    defaultEnabled: process.env.NODE_ENV === 'development',
    category: 'development',
  },

  // === Audio & Visual ===
  SOUND_EFFECTS: {
    id: 'SOUND_EFFECTS',
    name: 'Soundeffekte',
    description: 'Aktiviert alle Spielsounds',
    defaultEnabled: true,
    category: 'audio',
    persistent: true, // Wird in localStorage gespeichert
  },

  ANIMATIONS: {
    id: 'ANIMATIONS',
    name: 'Animationen',
    description: 'Aktiviert Spielanimationen (Karten fliegen, etc.)',
    defaultEnabled: true,
    category: 'visual',
    persistent: true,
  },

  FLYING_CARDS: {
    id: 'FLYING_CARDS',
    name: 'Fliegende Karten',
    description: 'Karten fliegen beim Spielen zur Mitte',
    defaultEnabled: true,
    category: 'visual',
    requires: ['ANIMATIONS'],
  },

  // === Experimentelle Features ===
  EXTENDED_SHOP_TIME: {
    id: 'EXTENDED_SHOP_TIME',
    name: 'Erweiterte Shop-Zeit',
    description: 'Verlängert die Shop-Zeit auf 10 Minuten (für Tests)',
    defaultEnabled: false,
    category: 'experimental',
  },

  TOAST_NOTIFICATIONS: {
    id: 'TOAST_NOTIFICATIONS',
    name: 'Toast-Benachrichtigungen',
    description: 'Leichte Toast-Meldungen statt großer Splash-Screens für Siegel-Effekte',
    defaultEnabled: true,
    category: 'experimental',
  },

  EVENT_LOG: {
    id: 'EVENT_LOG',
    name: 'Event-Log',
    description: 'Zeigt alle Spielereignisse in einem scrollbaren Panel',
    defaultEnabled: true,
    category: 'experimental',
  },
};

/**
 * Feature-Kategorien für UI-Gruppierung
 */
export const FEATURE_CATEGORIES = {
  phases: {
    id: 'phases',
    name: 'Spielphasen',
    description: 'Features für verschiedene Spielphasen',
  },
  gameplay: {
    id: 'gameplay',
    name: 'Gameplay',
    description: 'Kern-Gameplay-Features',
  },
  development: {
    id: 'development',
    name: 'Entwicklung',
    description: 'Features für Entwicklung und Testing',
  },
  audio: {
    id: 'audio',
    name: 'Audio',
    description: 'Sound-bezogene Features',
  },
  visual: {
    id: 'visual',
    name: 'Visuell',
    description: 'Visuelle Effekte und Animationen',
  },
  experimental: {
    id: 'experimental',
    name: 'Experimentell',
    description: 'Experimentelle Features (können instabil sein)',
  },
};

/**
 * Preset-Konfigurationen
 */
export const FEATURE_PRESETS = {
  production: {
    name: 'Produktion',
    description: 'Standard-Konfiguration für Live-Spiel',
    features: {
      TEST_MODE: false,
      DEV_TOOLS: false,
      DEBUG_LOGGING: false,
      PRE_GAME_SHOP: false,
      EXTENDED_SHOP_TIME: false,
    },
  },
  development: {
    name: 'Entwicklung',
    description: 'Konfiguration für Entwicklung',
    features: {
      DEV_TOOLS: true,
      DEBUG_LOGGING: true,
    },
  },
  testing: {
    name: 'Test-Modus',
    description: 'Konfiguration für lokales Testen',
    features: {
      TEST_MODE: true,
      PRE_GAME_SHOP: true,
      DEV_TOOLS: true,
      DEBUG_LOGGING: true,
      EXTENDED_SHOP_TIME: true,
    },
  },
  minimal: {
    name: 'Minimal',
    description: 'Minimale Features für Performance-Tests',
    features: {
      ANIMATIONS: false,
      FLYING_CARDS: false,
      SOUND_EFFECTS: false,
    },
  },
};

/**
 * Hilfsfunktion: Alle Feature-IDs einer Kategorie
 */
export function getFeaturesByCategory(categoryId) {
  return Object.values(FEATURES).filter(f => f.category === categoryId);
}

/**
 * Hilfsfunktion: Feature-Abhängigkeiten auflösen
 */
export function resolveFeatureDependencies(featureId, enabled) {
  const feature = FEATURES[featureId];
  const changes = { [featureId]: enabled };

  if (enabled) {
    // Aktiviert automatisch abhängige Features
    if (feature.activates) {
      feature.activates.forEach(depId => {
        changes[depId] = true;
      });
    }
  } else {
    // Deaktiviert Features, die dieses Feature benötigen
    Object.values(FEATURES).forEach(f => {
      if (f.requires?.includes(featureId)) {
        changes[f.id] = false;
      }
    });
  }

  return changes;
}

export default FEATURES;
